'use client';

import { useAuth } from '@/backend/context/auth-management';
import { ROUTES } from '@/lib/routes';
import { useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router';

// Modales
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { DolarCalculatorDialog } from './global-modals/CalculatorModal';

// Iconos
import {
  Banknote,
  Calculator,
  Home,
  Plus,
  Settings,
  Users2,
  X,
} from 'lucide-react';

import { useMoney } from '@/backend/firebase/hooks';
import { useFormGuardStore } from '@/backend/store/useFormGuardStore';
import { PostHogPageView } from '@/components/providers/posthog-pageview';
import { cn } from '@/lib/utils';
import { AnalyticsService } from '@/shared/analytics/analytics-service';

export const SystemLayout = () => {
  const { profile, adminSettings } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Global States
  const [isCalcOpen, setIsCalcOpen] = useState(false);
  const [isActionSheetOpen, setIsActionSheetOpen] = useState(false);

  const { rates } = useMoney();

  // Form Guard State
  const isDirty = useFormGuardStore((s) => s.isAnyFormDirty);

  const isSuspended = adminSettings?.accountStatus === 'suspended';
  const expiresAt = adminSettings?.expiresAt;
  const expiryDate =
    expiresAt?.toDate?.() ||
    (typeof expiresAt === 'number' ? new Date(expiresAt) : null);
  const isExpired = expiryDate && expiryDate.getTime() < Date.now();
  const isBlocked = isSuspended || isExpired;

  if (isBlocked) {
    return (
      <MobileFrame isDirty={isDirty}>
        <main className="flex-1 overflow-y-auto">
          <Outlet />
        </main>
      </MobileFrame>
    );
  }

  // Navigation Schema for TuNomina
  const NAV_ITEMS = [
    { path: ROUTES.DASHBOARD.PRINCIPAL, label: 'Inicio', icon: Home },
    { path: ROUTES.EMPLOYEES.ROOT, label: 'Empleados', icon: Users2 },
    { path: ROUTES.PAYROLL.ROOT, label: 'Nómina', icon: Banknote },
    { path: ROUTES.REPORTS.ROOT, label: 'Reportes', icon: Calculator },
    { path: ROUTES.SETTINGS.ROOT, label: 'Ajustes', icon: Settings },
  ];

  const mobileNavLeft = NAV_ITEMS.slice(0, 2);
  const mobileNavRight = NAV_ITEMS.slice(2);

  // ... (keeping everything else)

  return (
    <>
      <MobileFrame isDirty={isDirty}>
        <main 
          className={cn(
            "flex-1 overflow-y-auto scroll-smooth px-4 pt-4"
          )}
        >
          <Outlet />
        </main>

        {/* BOTTOM NAV */}
        <nav className="z-50 border-t border-zinc-200 bg-white pb-[env(safe-area-inset-bottom)]">
          <div className="flex h-14 items-center justify-around px-2">
            {mobileNavLeft.map((item) => (
              <NavIcon
                key={item.path}
                item={item}
                isActive={location.pathname.startsWith(item.path)}
                onClick={() => navigate(item.path)}
              />
            ))}

            {/* The Center Plus (+) button */}
            <button
              onClick={() => setIsActionSheetOpen(true)}
              className="relative flex -translate-y-3 items-center justify-center transition-transform active:scale-90"
            >
              <div className="rounded-2xl bg-zinc-900 p-3.5 text-white shadow-xl shadow-zinc-300">
                <Plus className="h-6 w-6" strokeWidth={3} />
              </div>
            </button>

            {mobileNavRight.map((item) => (
              <NavIcon
                key={item.path}
                item={item}
                isActive={location.pathname.startsWith(item.path)}
                onClick={() => navigate(item.path)}
              />
            ))}
          </div>
        </nav>
      </MobileFrame>

      {/* ACTION SHEET */}
      <Sheet open={isActionSheetOpen} onOpenChange={setIsActionSheetOpen}>
        <SheetContent
          side="bottom"
          className="mx-auto w-full border-none p-6 pb-12 outline-none sm:max-w-md rounded-t-[2rem]"
        >
          <div className="mb-8 flex items-center justify-between">
            <h2 className="text-xl font-black tracking-tighter text-zinc-900 uppercase italic">
              Nueva Acción
            </h2>
            <button
              onClick={() => setIsActionSheetOpen(false)}
              className="rounded-full bg-zinc-100 p-2 text-zinc-400 transition-transform hover:bg-zinc-200 hover:text-zinc-900 active:scale-95"
            >
              <X className="h-4 w-4" strokeWidth={3} />
            </button>
          </div>

          <div className="grid gap-3">
            <ActionCard
              icon={Users2}
              title="Registrar Empleado"
              subtitle="Agregar nuevo trabajador"
              color="bg-zinc-900 text-white"
              onClick={() => {
                setIsActionSheetOpen(false);
                navigate(ROUTES.EMPLOYEES.CREATE);
              }}
            />
            <ActionCard
              icon={Banknote}
              title="Nueva Nómina"
              subtitle="Generar lote de pagos"
              color="bg-emerald-50 text-emerald-600"
              onClick={() => {
                setIsActionSheetOpen(false);
                navigate(ROUTES.PAYROLL.CREATE);
              }}
            />
            <ActionCard
              icon={Calculator}
              title="Calculadora $ / Bs"
              subtitle="Conversión a tasa BCV"
              color="bg-amber-50 text-amber-600"
              onClick={() => {
                setIsActionSheetOpen(false);
                setIsCalcOpen(true);
              }}
            />
          </div>
        </SheetContent>
      </Sheet>

      <DolarCalculatorDialog
        isOpen={isCalcOpen}
        onOpenChange={setIsCalcOpen}
        tasaOficial={rates?.currentTasa || 0}
      />
    </>
  );
};

// We define a Shared Wrapper to ensure Mobile Parity on Desktop
const MobileFrame = ({
  children,
  className,
  isDirty,
}: {
  children: React.ReactNode;
  className?: string;
  isDirty?: boolean;
}) => (
  <div className="fixed inset-0 z-0 flex items-center justify-center overflow-hidden bg-zinc-950 font-sans text-zinc-900 selection:bg-zinc-900 selection:text-white transition-colors">
    <PostHogPageView />
    <div
      className={cn(
        'relative flex h-full w-full max-w-md flex-col overflow-hidden border-x border-white/5 shadow-[0_0_100px_rgba(0,0,0,0.5)] transition-all duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] bg-zinc-50',
        isDirty && 'ring-2 ring-amber-400 ring-inset',
        className,
      )}
    >
      {children}
    </div>
  </div>
);

// --- HELPER COMPONENTS ---

const NavIcon = ({
  item,
  isActive,
  onClick,
}: {
  item: any;
  isActive: boolean;
  onClick: () => void;
}) => (
  <button
    onClick={onClick}
    className="relative flex h-full w-full max-w-[64px] flex-col items-center justify-center gap-0.5 transition-transform active:scale-90"
  >
    <item.icon
      className={cn(
        'h-[22px] w-[22px] transition-all',
        isActive ? 'text-zinc-900' : 'text-zinc-400',
      )}
      strokeWidth={isActive ? 2.5 : 1.5}
    />
    <span
      className={cn(
        'text-[10px] font-bold tracking-tight transition-colors',
        isActive ? 'text-zinc-900' : 'text-zinc-400',
      )}
    >
      {item.label}
    </span>
  </button>
);

const ActionCard = ({ icon: Icon, title, subtitle, color, onClick }: any) => (
  <button
    onClick={onClick}
    className="group flex w-full items-center gap-4 rounded-2xl border border-zinc-100 bg-white p-4 text-left shadow-sm transition-all active:scale-[0.98] active:bg-zinc-50"
  >
    <div className={cn('rounded-xl p-3', color)}>
      <Icon className="h-6 w-6" strokeWidth={2.5} />
    </div>
    <div>
      <h3 className="text-sm font-bold tracking-tight text-zinc-900">
        {title}
      </h3>
      <p className="text-xs font-medium text-zinc-500">{subtitle}</p>
    </div>
  </button>
);
